/**
 * Mail 系データの hooks (mails / projects)。
 * テンプレート / プレセットは usePromptData.ts に統合済み。
 * 仕様書: docs/01_spec/spec_mail_system.md B-2 / docs/01_spec/spec_common.md C-X 統一プロンプト管理
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as limitFn,
  orderBy,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';

import { safeAddDoc, safeDeleteDoc, safeUpdateDoc } from '@/lib/safeWrite';

import type { Mail, MailProject } from '@goenhub/shared';

import { db } from '@/lib/firebase';

// ─────────────────────────────────────────────
// Mails
// ─────────────────────────────────────────────
export interface MailRecord extends Mail {
  id: string;
}

export function useMails(options?: {
  deleted?: boolean;
  status?: string;
  projectId?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['mails', options],
    queryFn: async (): Promise<MailRecord[]> => {
      // projectId 指定時は array-contains + orderBy のインデックスを使い、
      // _deleted / status はクライアント側フィルタ（複合インデックス追加回避）
      const conditions = options?.projectId
        ? [where('projectIds', 'array-contains', options.projectId)]
        : [where('_deleted', '==', options?.deleted ?? false)];
      if (options?.status && !options?.projectId) {
        conditions.push(where('status', '==', options.status));
      }
      const snap = await getDocs(
        query(
          collection(db, 'mails'),
          ...conditions,
          orderBy('createdAt', 'desc'),
          limitFn(options?.limit ?? 100),
        ),
      );
      let rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Mail) }));
      if (options?.projectId) {
        rows = rows.filter((r) => r._deleted === (options?.deleted ?? false));
        if (options?.status) rows = rows.filter((r) => r.status === options.status);
      }
      return rows;
    },
  });
}

export function useCreateMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<Mail>) => {
      const ref = await safeAddDoc(collection(db, 'mails'), {
        ...data,
        status: data.status ?? 'draft',
        _deleted: false,
        _deletedAt: null,
        sentAt: null,
        sentTo: null,
        projectIds: data.projectIds ?? [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return ref.id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mails'] }),
  });
}

/**
 * 既存メールを複製。Phase 22a / spec_mail_system.md B-14.2.1。
 * A/B テスト用途：複製してから CTA URL を編集する想定。
 */
export function useDuplicateMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (originalId: string): Promise<string> => {
      const snap = await getDoc(doc(db, 'mails', originalId));
      if (!snap.exists()) throw new Error('複製元のメールが見つかりません');
      const original = snap.data() as Mail;
      const ref = await safeAddDoc(collection(db, 'mails'), {
        subject: `(コピー) ${original.subject ?? ''}`,
        sections: original.sections ?? [],
        overview: original.overview ?? '',
        field_comments: original.field_comments ?? {},
        templateId: original.templateId ?? '',
        formData: original.formData ?? {},
        lang: original.lang ?? 'ja',
        modelUsed: original.modelUsed ?? '',
        sectionComments: original.sectionComments ?? {},
        note: original.note ?? '',
        status: 'draft',
        projectIds: original.projectIds ?? [],
        sentAt: null,
        sentTo: null,
        _deleted: false,
        _deletedAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return ref.id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mails'] }),
  });
}

export function useUpdateMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; data: Partial<Mail> }) => {
      await safeUpdateDoc(doc(db, 'mails', args.id), {
        ...args.data,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mails'] }),
  });
}

export function useSoftDeleteMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await safeUpdateDoc(doc(db, 'mails', id), {
        _deleted: true,
        _deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mails'] }),
  });
}

export function useRestoreMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await safeUpdateDoc(doc(db, 'mails', id), {
        _deleted: false,
        _deletedAt: null,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mails'] }),
  });
}

export function useHardDeleteMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await safeDeleteDoc(doc(db, 'mails', id));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mails'] }),
  });
}

// ─────────────────────────────────────────────
// Projects
// ─────────────────────────────────────────────
export interface MailProjectRecord extends MailProject {
  id: string;
}

export function useMailProjects() {
  return useQuery({
    queryKey: ['mail_projects'],
    queryFn: async (): Promise<MailProjectRecord[]> => {
      const snap = await getDocs(
        query(collection(db, 'mail_projects'), orderBy('createdAt', 'desc')),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as MailProject) }));
    },
  });
}

export function useCreateMailProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<MailProject>) => {
      await safeAddDoc(collection(db, 'mail_projects'), {
        name: data.name ?? '',
        color: data.color ?? '#4a7cff',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail_projects'] }),
  });
}

export function useDeleteMailProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await safeDeleteDoc(doc(db, 'mail_projects', id));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail_projects'] }),
  });
}
